import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { HEURES, toYmd } from '../../utils/planningTime';
import './MiniGrid.css';

// Nom AVANT prénom, comme dans PlanningAtelier (demande métier du 2026-08-09).
const getStaffLabel = (s) => [s.nom, s.prenom].filter(Boolean).join(' ') || s.nom || `#${s.id}`;
const toHM = (t) => (t ? String(t).slice(0, 5) : null);

const shiftDate = (iso, delta) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return toYmd(d);
};

const formatDateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Aperçu compact et glisser-déposer du planning de réception, embarqué dans la
// modale d'intervention. Volontairement plus simple que la grille principale de
// PlanningAtelier.js (pas de menu contextuel, pas de vue semaine) — chaque
// cellule (créneau × réceptionniste) est un vrai drop-target HTML natif, ce qui
// évite le calcul de position par pixel de la grille principale.
//
// Navigation jour par jour : la journée correspondant à `date` (celle du
// formulaire) s'affiche sans appel réseau supplémentaire (données déjà
// chargées par PlanningAtelier.js, transmises en prop). Naviguer vers un autre
// jour déclenche un chargement ciblé sur ce seul jour.
// `current` : le créneau de réception en cours de saisie ({ date, heure, poste, label }),
// dessiné en fantôme comme dans MiniAtelierGrid. Le RDV sauvegardé du dossier
// édité est masqué (représenté par le fantôme).
export default function MiniReceptionGrid({ date, receptionnaires = [], rdvReception = [], heures: heuresProp = null, currentInterventionId, current = null, onMove, onPickSlot }) {
  // Créneaux ouverts fournis par le Planning (plage d'ouverture des ateliers) ;
  // repli sur HEURES (07:00→16:45) pour rester utilisable isolément.
  const heures = (Array.isArray(heuresProp) && heuresProp.length > 0) ? heuresProp : HEURES;
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null); // `${posteId}-${heure}`
  const [viewDate, setViewDate] = useState(date);
  const [remoteRdv, setRemoteRdv] = useState(null);
  // ⚠️ Seconde source, indispensable : voir le commentaire du chargement.
  const [remoteIntv, setRemoteIntv] = useState(null);
  const [loading, setLoading] = useState(false);
  // Congés / absences des réceptionnaires (même source que la grille atelier) :
  // sans elle, un réceptionnaire en congé apparaissait « libre » dans l'aperçu.
  const [absences, setAbsences] = useState([]);

  useEffect(() => { setViewDate(date); setRemoteRdv(null); setRemoteIntv(null); }, [date]);

  useEffect(() => {
    if (!viewDate) return;
    let cancelled = false;
    api.get('/planning/absences', { params: { date_from: viewDate, date_to: viewDate } })
      .then(r => { if (!cancelled) setAbsences(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [viewDate]);

  // ⚠️ La grille interroge le serveur pour TOUTE date affichée, y compris celle
  // du dossier — c'est le correctif du 2026-09-04.
  //
  // Avant, elle ne le faisait que si l'on naviguait vers une AUTRE date, et se
  // contentait sinon de `rdvReception`, la liste que le Planning lui passe. Or
  // le Planning ne charge que la fenêtre de la journée AFFICHÉE À L'ÉCRAN : dès
  // que la réception du dossier en sortait — un rendez-vous pris pour la semaine
  // suivante, un dossier ouvert depuis une autre journée — la grille était
  // VIDE alors que la journée était pleine. C'était l'irritant signalé.
  //
  // La liste reçue reste affichée pendant la requête : pas de clignotement à
  // vide, et le repli en cas d'échec est ce qu'on avait déjà.
  useEffect(() => {
    if (!viewDate) { setRemoteRdv(null); setRemoteIntv(null); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get('/planning/rdv', { params: { date_from: viewDate, date_to: viewDate } })
        .then(r => r.data.rdv || []).catch(() => []),
      // ⚠️ DEUXIÈME source, et c'est elle qui manquait (2026-09-05).
      // Une réception n'existe pas toujours comme ligne de `planning_rdv` : les
      // dossiers importés de Mécaplanning portent leur heure DANS
      // l'intervention (`heure_reception` + `poste_reception`) et ne créent
      // aucun rendez-vous de réception. Mesuré le 04/09 : CENTRAUTODIS a
      // **0 planning_rdv pour 69 dossiers** avec une heure de réception — la
      // grille y était donc vide en permanence, quelle que soit la date.
      // L'écran Réception du Planning fusionne déjà les deux ; cette grille,
      // non. C'est le même rapprochement, refait ici.
      api.get('/planning/interventions', {
        params: { date_from: viewDate, date_to: viewDate, date_field: 'reception' },
      }).then(r => r.data.interventions || []).catch(() => []),
    ])
      .then(([rdv, intv]) => {
        if (cancelled) return;
        setRemoteRdv(rdv);
        setRemoteIntv(intv);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewDate]);

  // Congés / absences du jour affiché → cartes neutres par réceptionnaire.
  // Même logique que MiniAtelierGrid (absences seules ici : les pauses de la
  // grille atelier ne s'appliquent pas aux postes de réception). Défini AVANT
  // les retours anticipés — un hook ne doit jamais être appelé conditionnellement.
  const { indispoCards, indispoSlots } = useMemo(() => {
    const cards = [];
    const slots = new Set();
    const push = (posteId, i0, iEnd, label) => {
      if (i0 < 0) i0 = 0;
      if (iEnd > heures.length) iEnd = heures.length;
      if (iEnd <= i0) return;
      cards.push({ poste_id: posteId, heure_debut: heures[i0], span: iEnd - i0, label });
      for (let k = i0; k < iEnd; k++) slots.add(`${posteId}-${heures[k]}`);
    };
    const idx = (hm) => {
      if (!hm) return -1;
      const i = heures.indexOf(hm);
      if (i !== -1) return i;
      return heures.findIndex(h => h >= hm);
    };
    const ids = new Set(receptionnaires.map(r => r.id));
    for (const a of absences) {
      if (!ids.has(a.staff_id)) continue;
      const dd = String(a.date_debut || '').slice(0, 10);
      const df = String(a.date_fin || '').slice(0, 10);
      if (dd && df && !(dd <= viewDate && viewDate <= df)) continue;
      const hd = toHM(a.heure_debut);
      const hf = toHM(a.heure_fin);
      if (!hd && !hf) { push(a.staff_id, 0, heures.length, 'Absent'); continue; }
      const i0 = hd ? idx(hd) : 0;
      const iEnd = hf ? idx(hf) : heures.length;
      push(a.staff_id, i0, iEnd < 0 ? heures.length : iEnd, 'Indispo');
    }
    return { indispoCards: cards, indispoSlots: slots };
  }, [absences, receptionnaires, viewDate, heures]);

  if (!date) {
    return <div className="mg-empty">Renseignez une date de réception pour voir l'aperçu.</div>;
  }
  if (receptionnaires.length === 0) {
    return <div className="mg-empty">Aucun réceptionniste configuré.</div>;
  }

  // Tant que la réponse n'est pas là, on montre ce que le Planning a passé —
  // souvent juste, parfois vide, jamais faux.
  const sourceRdvs = remoteRdv !== null ? remoteRdv : (viewDate === date ? rdvReception : []);
  const rdvDuJour = sourceRdvs.filter(r => r.date === viewDate);

  // Les dossiers dont l'heure de réception ne vit QUE dans l'intervention. On
  // écarte ceux déjà couverts par un rendez-vous explicite, sinon le même
  // client apparaîtrait deux fois sur le même créneau.
  const idsCouverts = new Set(rdvDuJour.map(r => r.intervention_id).filter(Boolean));
  const intvSansRdv = (remoteIntv || [])
    .filter(i => i.heure_reception && i.poste_reception && !i.is_complement && !idsCouverts.has(i.id))
    .map(i => ({
      id: `int-${i.id}`,
      date: viewDate,
      heure: i.heure_reception,
      poste: i.poste_reception,
      client: i.client,
      client_nom: i.client_nom,
      client_prenom: i.client_prenom,
      intervention_id: i.id,
      // Pas une ligne de `planning_rdv` : le glisser-déposer, qui déplace un
      // rendez-vous par son id, n'a rien à déplacer ici.
      _fromIntervention: true,
    }));

  const dayRdvs = [...rdvDuJour, ...intvSansRdv]
    .filter(r => !(current && currentInterventionId && r.intervention_id === currentInterventionId));
  const ghostVisible = current && current.poste && current.heure && current.date === viewDate;

  const handleDrop = (e, posteId, heure) => {
    e.preventDefault();
    setDragOverCell(null);
    const id = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!id) return;
    setDraggingId(null);
    onMove(id, { heure, poste: posteId });
  };

  return (
    <div>
      <div className="mg-nav">
        <button type="button" className="mg-nav-btn" onClick={() => setViewDate(v => shiftDate(v, -1))} title="Jour précédent">‹</button>
        <span className="mg-nav-date">{formatDateLabel(viewDate)}</span>
        <button type="button" className="mg-nav-btn" onClick={() => setViewDate(v => shiftDate(v, 1))} title="Jour suivant">›</button>
        {viewDate !== date && (
          <button type="button" className="mg-nav-today" onClick={() => setViewDate(date)}>↺ Date du formulaire</button>
        )}
        {loading && <span className="mg-nav-loading">Chargement…</span>}
      </div>
      <div className="mg-wrapper">
        <div className="mg-grid" style={{ gridTemplateColumns: `56px repeat(${receptionnaires.length}, minmax(90px, 1fr))` }}>
          <div className="mg-corner" />
          {receptionnaires.map(r => (
            <div key={r.id} className="mg-col-header">{getStaffLabel(r)}</div>
          ))}

          {heures.map(h => (
            <React.Fragment key={h}>
              <div className={`mg-time-cell${h.endsWith(':00') ? ' mg-full-hour' : ''}`}>
                {h.endsWith(':00') ? h : ''}
              </div>
              {receptionnaires.map(recep => {
                const items = dayRdvs.filter(r => r.poste === recep.id && r.heure === h);
                const cellKey = `${recep.id}-${h}`;
                const indispo = indispoSlots.has(cellKey);
                const indispoIci = indispoCards.filter(c => c.poste_id === recep.id && c.heure_debut === h);
                return (
                  <div
                    key={recep.id}
                    className={`mg-slot-cell${dragOverCell === cellKey ? ' mg-drag-over' : ''}${!indispo && items.length === 0 && onPickSlot ? ' mg-slot-clickable' : ''}`}
                    onDragOver={(e) => { if (indispo) return; e.preventDefault(); setDragOverCell(cellKey); }}
                    onDragLeave={() => setDragOverCell(prev => (prev === cellKey ? null : prev))}
                    onDrop={(e) => { if (!indispo) handleDrop(e, recep.id, h); }}
                    onClick={() => { if (!indispo && items.length === 0 && onPickSlot) onPickSlot(viewDate, h, recep.id); }}
                    title={!indispo && items.length === 0 && onPickSlot ? `Choisir ${h} · ${getStaffLabel(recep)} · ${formatDateLabel(viewDate)}` : undefined}
                  >
                    {indispoIci.map((c, ci) => (
                      <div key={`ind-${ci}`} className="mg-card mg-card-indispo" style={{ height: c.span * 16 - 2 }}
                        title={`${c.label} · ${getStaffLabel(recep)}`}>
                        {c.label}
                      </div>
                    ))}
                    {ghostVisible && Number(current.poste) === recep.id && current.heure === h && (
                      <div className="mg-card mg-card-current mg-card-ghost"
                        title={`${h} · ${current.label || 'Ce dossier'} (créneau en cours de saisie)`}>
                        {current.label || 'Ce dossier'}
                      </div>
                    )}
                    {items.map(r => {
                      const clientLabel = r.client || [r.client_prenom, r.client_nom].filter(Boolean).join(' ') || '—';
                      const isCurrent = currentInterventionId && r.intervention_id === currentInterventionId;
                      return (
                        <div
                          key={r.id}
                          className={`mg-card${isCurrent ? ' mg-card-current' : ''}${draggingId === r.id ? ' mg-dragging' : ''}${r._fromIntervention ? ' mg-card-fixe' : ''}`}
                          draggable={!r._fromIntervention}
                          onDragStart={(e) => {
                            if (r._fromIntervention) { e.preventDefault(); return; }
                            e.stopPropagation();
                            e.dataTransfer.setData('text/plain', String(r.id));
                            e.dataTransfer.effectAllowed = 'move';
                            setDraggingId(r.id);
                          }}
                          onDragEnd={() => setDraggingId(null)}
                          title={r._fromIntervention
                            ? `${h} · ${clientLabel} — réception portée par le dossier, non déplaçable ici`
                            : `${h} · ${clientLabel}`}
                        >
                          {clientLabel}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
